import React, { useEffect, useCallback } from 'react';
import { useQuery, useLazyQuery, gql } from '@apollo/client';
import Lottie from 'react-lottie-player';
import { toast } from 'react-toastify';
import { logger } from 'RootShared/utility/ErrorLogger';

import Input from 'Components/Input';
import useReducedErrors from 'Shared/hooks/useReducedErrors';
import animationData from 'Shared/spiner_blue.json';
import { CAN_EDIT_COMPANY } from '../../constants/constants';

export const queryGetCountriesData = gql`
  query CountriesDataQuery @api(name: root) {
    countries {
      name
      id
    }
  }
`;

export const queryGetCitiesData = gql`
  query CitiesDataQuery($limit: Int!, $offset: Int!, $term: String, $countryId: Int) @api(name: root) {
    cities(offset: $offset, limit: $limit, term: $term, countryId: $countryId) {
      id
      name
    }
  }
`;

const TabCompany = (props) => {
  const { data: dataCountries, loading: loadingCountries, error: errorCountries } = useQuery(queryGetCountriesData);

  const [getCities, { data: dataCities, refetch: refetchCities, error: errorCities }] = useLazyQuery(
    queryGetCitiesData
  );

  if (errorCountries) {
    logger.error(errorCountries);
    toast(errorCountries.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  if (errorCities) {
    logger.error(errorCities);
    toast(errorCities.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  const {
    formik: {
      values: { firstName, lastName, email, title, address1, zip, country, city, emailBusiness },
      errors,
      handleBlur,
      handleChange,
      setValues,
    },
    data,
  } = props;

  useEffect(() => {
    country !== null &&
      country.id &&
      getCities({ variables: { countryId: +country.id, offset: 0, limit: 20, term: 'A' } });
    country === null && getCities({ variables: { offset: 0, limit: 20, term: 'A' } });
  }, [country, dataCities, getCities]);

  useEffect(() => {
    if (data && data.user.customerprofile) {
      setValues(
        (({
          user: {
            email,
            id,
            customerprofile: {
              contact: {
                firstName,
                lastName,
                company: { country, city, title, address1, zip, email: emailBusiness },
              },
            },
          },
        }) => ({
          firstName,
          lastName,
          email,
          title,
          address1,
          zip,
          country,
          city,
          emailBusiness,
          id,
        }))(data)
      );
    }
  }, [data, setValues]);

  useEffect(() => {
    //cache data until user closes Profile tab
    const localStoragePersonalData = JSON.parse(localStorage.getItem('personalCustomerData'));
    if (localStoragePersonalData) {
      setValues(localStoragePersonalData);
    }
  }, [data, setValues]);

  useEffect(() => {
    localStorage.setItem(
      'personalCustomerData',
      JSON.stringify({
        firstName,
        lastName,
        email,
        title,
        address1,
        zip,
        country,
        city,
        emailBusiness,
      })
    );
  }, [address1, city, country, email, emailBusiness, firstName, lastName, title, zip]);

  const reducedErrors = useReducedErrors(errors);

  const onCountryChange = useCallback(
    (countrySelected) => {
      setValues({ ...props.formik.values, country: formatDataForQuery(countrySelected), city: null });
      getCities({ variables: { countryId: countrySelected.id, offset: 0, limit: 20, term: 'A' } });
    },
    [getCities, props.formik.values, setValues]
  );

  const onCityChange = useCallback(
    (citySelected) => {
      setValues({ ...props.formik.values, city: formatDataForQuery(citySelected) });
    },
    [props.formik.values, setValues]
  );

  const formatDataForSelect = (countries) =>
    countries.map((item) => {
      return item && { id: item.id, label: item.name, value: item.name };
    });

  const formatDataForQuery = (country) => {
    return { id: country.id, name: country.label };
  };

  if (loadingCountries) {
    return (
      <div className="tab-company-lottie">
        <Lottie
          className="lottie"
          loop={true}
          play={true}
          animationData={animationData}
          width={100}
          style={{ width: 100, height: 100, margin: '0px auto' }}
        />
      </div>
    );
  }

  const promiseOptions = (inputValue) =>
    new Promise((resolve) => {
      refetchCities({ countryId: country.id, offset: 0, limit: 20, term: inputValue }).then(({ data }) =>
        resolve(formatDataForSelect(data.cities))
      );
    });

  const isEditable = data.user.permissions.includes(CAN_EDIT_COMPANY);

  return (
    <div className="profile-tab customer-profile customer-profile-company" data-testid="profile-tab-company">
      <div className="profile-tab__section">
        <Input
          label="Company name"
          helperVariant={reducedErrors['title'] ? 'error' : 'message'}
          helper={reducedErrors['title']}
          id="title"
          fieldProps={{
            placeholder: 'Enter your company name',
            name: 'title',
            value: title,
            onChange: handleChange,
            onBlur: handleBlur,
          }}
          disabled={!isEditable}
        />
        <Input
          label="Email Business"
          helperVariant={reducedErrors['emailBusiness'] ? 'error' : 'message'}
          helper={reducedErrors['emailBusiness']}
          id="emailBusiness"
          fieldProps={{
            placeholder: 'Enter email business',
            name: 'emailBusiness',
            value: emailBusiness,
            onChange: handleChange,
            onBlur: handleBlur,
          }}
          disabled={!isEditable}
        />
      </div>
      <Input
        label="Country"
        variant="select"
        helper={reducedErrors['country']}
        helperVariant={reducedErrors['country'] ? 'error' : 'message'}
        callback={onCountryChange}
        options={formatDataForSelect(dataCountries ? dataCountries.countries : [])}
        fieldProps={{
          value: formatDataForSelect([country]) || formatDataForSelect(dataCountries ? dataCountries.countries : [])[0],
          name: 'country',
          placeholder: 'Select your country',
          onBlur: handleBlur('country'),
          isSearchable: true,
        }}
        disabled={!isEditable}
      />
      <Input
        label="City"
        variant="select"
        variantSelect="AsyncSelect"
        helper={reducedErrors['city']}
        helperVariant={reducedErrors['city'] ? 'error' : 'message'}
        callback={onCityChange}
        defaultOptions={formatDataForSelect(dataCities ? dataCities.cities : [])}
        loadOptions={promiseOptions}
        fieldProps={{
          value: formatDataForSelect([city]) || formatDataForSelect(dataCities ? dataCities.cities : [])[0],
          name: 'city',
          placeholder: 'Select your city',
          onBlur: handleBlur('city'),
          isSearchable: true,
        }}
        disabled={!isEditable}
      />

      <Input
        label="Сompany address"
        helperVariant={reducedErrors['address1'] ? 'error' : 'message'}
        helper={reducedErrors['address1']}
        id="address1"
        fieldProps={{
          placeholder: 'Enter company`s address',
          name: 'address1',
          value: address1,
          onChange: handleChange,
          onBlur: handleBlur,
        }}
        disabled={!isEditable}
      />
      <Input
        label="Zip/Postal code"
        helperVariant={reducedErrors['zip'] ? 'error' : 'message'}
        helper={reducedErrors['zip']}
        id="zip"
        fieldProps={{
          placeholder: 'Enter Zip or Postal code',
          name: 'zip',
          value: zip,
          onChange: handleChange,
          onBlur: handleBlur,
        }}
        disabled={!isEditable}
      />
    </div>
  );
};

export default TabCompany;
